import { useCallback, useMemo, useState } from "react";
import type { CanvasActionContextValue } from "./canvasContext.tsx";

/**
 * A collapsible column's open/closed state, plus canvas actions wired to drive
 * it. The card's size is answered here too, though as a constant rather than as
 * state — see the return.
 *
 * Both belong to the column rather than to the canvas drawn in it, for the same
 * reason. A thread's canvas persists visibility and display mode server-side,
 * and both defaults suit the thread's own page — where the canvas is a calendar
 * and a bounded card sits over it. Neither suits a blank column: reading the
 * first would fling the column open on every thread the user glanced at, and the
 * second would open a card inside a column barely wider than the card, framed
 * against nothing. So the host asks each question once and answers it here,
 * whichever canvas is supplying the content.
 *
 * Presenting reveals the column. Without that a card pushed into a collapsed one
 * is held by the stack and drawn by nothing, and the click that presented it
 * reads as broken.
 *
 * Every other action passes straight through, so the canvas behind this stays
 * the one source of what is on it.
 */
export const useCanvasColumn = (actions: CanvasActionContextValue) => {
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  // Deliberately leaves the stack alone. Collapsing is a statement about the
  // column, not about what it holds, so reopening shows what was there rather
  // than an empty canvas — and a thread's stack is persisted, so dropping it
  // here would throw away the artifact the agent presented.
  const close = useCallback(() => setIsOpen(false), []);

  const columnActions = useMemo<CanvasActionContextValue>(
    () => ({
      ...actions,
      resetTo: (urn: string) => {
        const presented = actions.resetTo(urn);
        // Only once the token parses. A URN nothing can render presents
        // nothing, and revealing the column to show an empty canvas is worse
        // than leaving it shut.
        if (presented) setIsOpen(true);
        return presented;
      },
      push: (urn: string) => {
        const result = actions.push(urn);
        // Reveal when the canvas is holding what was asked for: the layer just
        // pushed, or — for `duplicate` — the one already on top, since clicking
        // a link to the card that is already showing is an ordinary thing to do
        // and answering it with a collapsed column reads as a broken click.
        //
        // `max-depth` is deliberately not in that set. It declined to add a
        // layer *and* the target is not what is showing, so opening the column
        // would present an unrelated card as though it were the answer.
        if (result === "pushed" || result === "duplicate") setIsOpen(true);
        return result;
      },
      // The card's close button, its scrim and Escape all clear the stack, and
      // clearing it empties the column — so the same gesture collapses it.
      // Leaving it open would answer a dismissal with a blank panel still
      // holding the width. Back is the depth-wise gesture and pops one layer;
      // it does not come through here, so a card with another beneath it still
      // reveals that one rather than closing the column.
      clear: () => {
        actions.clear();
        setIsOpen(false);
      },
      // Omitted rather than stubbed, so the card suppresses its own toggle the
      // way it already does on every surface that cannot answer this. Passing a
      // function that does nothing would leave the control on screen, wired to
      // silence. A thread's own screen, where a card does sit over a calendar
      // and the choice means something, keeps its own.
      setDisplayMode: undefined,
      open,
      close,
    }),
    [actions, open, close],
  );

  // A constant rather than state: the column is the card's frame, so the card
  // fills it, and there is no second size for anything to choose.
  return { isOpen, isFullscreen: true, open, close, actions: columnActions };
};
